import { useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  MultiCombobox,
  type MultiComboboxOption,
  Select,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import type { MarketingEmailAccount } from '@/lib/types';
import { useUserOptions } from '@/lib/use-user-options';
import {
  ENCRYPTION_NOT_CONFIGURED_CODE,
  ENCRYPTION_NOT_CONFIGURED_MESSAGE,
  IMAP_PORT_PRESET,
  IMAP_SECURITY_OPTIONS,
  KNOWN_WRITE_KEYS,
  LIMITS,
  MAX_LENGTHS,
  SMTP_PORT_PRESET,
  SMTP_SECURITY_OPTIONS,
  buildCreatePayload,
  buildUpdatePayload,
  normaliseErrorPaths,
  portAfterSecurityChange,
  staffLabel,
  toFormValues,
  validate,
  type EmailAccountFormValues,
  type ImapSecurity,
  type SmtpSecurity,
} from './email-account-rules';

export interface EmailAccountFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create. */
  account?: MarketingEmailAccount;
  onSaved: () => void;
}

/**
 * Create or edit a shared sending account.
 *
 * ⛔ **Passwords never come back from the server**, so both password boxes open
 * empty on edit and an empty box means "keep the stored one" — said only in the
 * placeholder. The key is sent only when typed into. The IMAP password can also
 * be removed outright, which sends an explicit `null`.
 */
export function EmailAccountFormModal({ open, onClose, account, onSaved }: EmailAccountFormModalProps) {
  if (!open) return null;
  return <EmailAccountForm key={account?.id ?? 'new'} onClose={onClose} account={account} onSaved={onSaved} />;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="m-0 grid min-w-0 gap-3 border-0 border-t border-border p-0 pt-4 first:border-t-0 first:pt-0">
      <legend className="mb-1 p-0 text-xs font-semibold text-fg-secondary">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function EmailAccountForm({
  onClose,
  account,
  onSaved,
}: {
  onClose: () => void;
  account?: MarketingEmailAccount;
  onSaved: () => void;
}) {
  const formId = useId();
  const firstFieldRef = useRef<HTMLElement | null>(null);
  const isEdit = account !== undefined;

  const baseline = useMemo(() => toFormValues(account), [account]);
  const [values, setValues] = useState<EmailAccountFormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);
  const staff = useUserOptions(true);

  const updatePayload = useMemo(
    () => (account ? buildUpdatePayload(values, baseline, account) : null),
    [account, values, baseline],
  );
  const isDirty = isEdit ? Object.keys(updatePayload ?? {}).length > 0 : true;

  function setField<K extends keyof EmailAccountFormValues>(key: K, next: EmailAccountFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  function setSmtpSecurity(next: SmtpSecurity) {
    setValues((prev) => ({
      ...prev,
      smtpSecurity: next,
      smtpPort: portAfterSecurityChange(prev.smtpPort, SMTP_PORT_PRESET[next], SMTP_PORT_PRESET),
    }));
  }

  function setImapSecurity(next: ImapSecurity | '') {
    setValues((prev) => ({
      ...prev,
      imapSecurity: next,
      imapPort:
        next === ''
          ? prev.imapPort
          : portAfterSecurityChange(prev.imapPort, IMAP_PORT_PRESET[next], IMAP_PORT_PRESET),
    }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();

    const found = validate(values, {
      isEdit,
      hasSmtpPassword: account?.hasSmtpPassword ?? false,
    });
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    try {
      if (account) {
        const payload = buildUpdatePayload(values, baseline, account);
        if (Object.keys(payload).length === 0) {
          setFormError('Nothing has changed yet.');
          setSubmitting(false);
          return;
        }
        await api.updateMarketingEmailAccount(account.id, payload);
      } else {
        await api.createMarketingEmailAccount(buildCreatePayload(values));
      }
      onSaved();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 503 && caught.code === ENCRYPTION_NOT_CONFIGURED_CODE) {
          setFormError(ENCRYPTION_NOT_CONFIGURED_MESSAGE);
        } else if (caught.status === 403) {
          setFormError(
            caught.message || 'Your account is not allowed to change email accounts. Nothing was saved.',
          );
        } else {
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          // Indexed paths (`allowedUserIds.1`) never match the list above, so
          // they arrive in `unroutable`; try folding them onto their control.
          const folded = normaliseErrorPaths(caught.fieldErrors);
          const extra: Record<string, string> = {};
          for (const key of KNOWN_WRITE_KEYS) {
            const message = folded[key];
            if (!(key in fields) && message !== undefined) extra[key] = message;
          }
          const marked = { ...fields, ...extra };
          setServerErrors(marked);
          const banner = unroutable.filter(
            (line) => !Object.values(extra).some((message) => line.includes(message)),
          );
          if (banner.length > 0) setFormError(banner.join(' '));
          else if (Object.keys(marked).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Nothing was saved.');
      }
      setSubmitting(false);
    }
  }

  const userOptions: MultiComboboxOption[] = useMemo(() => {
    const options = staff.users.map((user) => ({
      value: String(user.id),
      label: staffLabel(user),
      hint: user.email,
    }));
    // A stored allowed user the directory page did not return is injected
    // back, or the chip would vanish and the next save would drop them.
    const known = new Set(options.map((option) => option.value));
    for (const user of account?.allowedUsers ?? []) {
      if (!known.has(String(user.id))) {
        options.push({ value: String(user.id), label: staffLabel(user), hint: user.email });
      }
    }
    return options;
  }, [staff.users, account]);

  const usersError = staff.failed
    ? 'The staff list could not be read, so nobody can be added. Close this and try again.'
    : errors.allowedUserIds;

  const imapOn = values.imapHost.trim() !== '';
  const fieldErrorCount = Object.keys(errors).length;

  function textField(
    key: keyof EmailAccountFormValues,
    label: string,
    options: {
      required?: boolean;
      type?: string;
      maxLength?: number;
      placeholder?: string;
      inputMode?: 'numeric' | 'email' | 'text';
      autoComplete?: string;
      span?: boolean;
      name?: string;
    } = {},
  ) {
    const value = values[key];
    return (
      <div className={options.span ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
        <Field label={label} required={options.required} error={errors[key]}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              name={options.name ?? key}
              type={options.type ?? 'text'}
              value={typeof value === 'string' ? value : ''}
              maxLength={options.maxLength}
              placeholder={options.placeholder}
              inputMode={options.inputMode}
              autoComplete={options.autoComplete ?? 'off'}
              invalid={Boolean(errors[key])}
              aria-describedby={describedBy}
              onChange={(event) => setField(key, event.target.value as never)}
            />
          )}
        </Field>
      </div>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={firstFieldRef}
      title={account ? `Edit ${account.name}` : 'Add email account'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={submitting} disabled={!isDirty}>
            {isEdit ? 'Save changes' : 'Add account'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <Section title="Sender">
          <div
            className="grid gap-3 sm:grid-cols-2"
            ref={(node) => {
              firstFieldRef.current = node?.querySelector('input[name="name"]') ?? null;
            }}
          >
            {textField('name', 'Account name', {
              required: true,
              maxLength: MAX_LENGTHS.name,
              span: true,
            })}
            {textField('fromName', 'From name', {
              required: true,
              maxLength: MAX_LENGTHS.fromName,
            })}
            {textField('fromEmail', 'From email', {
              required: true,
              type: 'email',
              maxLength: MAX_LENGTHS.email,
            })}
            {textField('replyTo', 'Reply-to', {
              type: 'email',
              maxLength: MAX_LENGTHS.email,
              span: true,
            })}
          </div>
        </Section>

        <Section title="SMTP">
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('smtpHost', 'Host', {
              required: true,
              maxLength: MAX_LENGTHS.host,
              span: true,
            })}
            <div className="min-w-0">
              <Field label="Security" required error={errors.smtpSecurity}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Select
                    id={id}
                    value={values.smtpSecurity}
                    invalid={Boolean(errors.smtpSecurity)}
                    aria-describedby={describedBy}
                    onChange={(event) => setSmtpSecurity(event.target.value as SmtpSecurity)}
                  >
                    {SMTP_SECURITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            {textField('smtpPort', 'Port', { required: true, inputMode: 'numeric' })}
            {textField('smtpUsername', 'Username', {
              required: true,
              maxLength: MAX_LENGTHS.username,
              autoComplete: 'off',
            })}
            {textField('smtpPassword', 'Password', {
              required: !isEdit,
              type: 'password',
              autoComplete: 'new-password',
              placeholder: isEdit
                ? account?.hasSmtpPassword
                  ? 'Leave blank to keep the current password'
                  : 'No password stored'
                : undefined,
            })}
          </div>
        </Section>

        <Section title="IMAP for bounces & replies">
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('imapHost', 'Host', {
              maxLength: MAX_LENGTHS.host,
              span: true,
            })}
            {imapOn && (
              <>
                <div className="min-w-0">
                  <Field label="Security" required error={errors.imapSecurity}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Select
                        id={id}
                        value={values.imapSecurity}
                        invalid={Boolean(errors.imapSecurity)}
                        aria-describedby={describedBy}
                        onChange={(event) => setImapSecurity(event.target.value as ImapSecurity | '')}
                      >
                        <option value="">Choose…</option>
                        {IMAP_SECURITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>
                {textField('imapPort', 'Port', { required: true, inputMode: 'numeric' })}
                {textField('imapUsername', 'Username', {
                  required: true,
                  maxLength: MAX_LENGTHS.username,
                })}
                {textField('imapPassword', 'Password', {
                  type: 'password',
                  autoComplete: 'new-password',
                  placeholder:
                    isEdit && account?.hasImapPassword && !values.clearImapPassword
                      ? 'Leave blank to keep the current password'
                      : undefined,
                })}
                {isEdit && account?.hasImapPassword && (
                  <div className="min-w-0 sm:col-span-2">
                    <Checkbox
                      label="Remove the stored IMAP password"
                      checked={values.clearImapPassword}
                      disabled={values.imapPassword !== ''}
                      onChange={(event) => setField('clearImapPassword', event.target.checked)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Section>

        <Section title="Limits">
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('dailyLimit', 'Emails per day', {
              required: true,
              inputMode: 'numeric',
              placeholder: LIMITS.dailyDefault,
            })}
            {textField('perMinuteLimit', 'Emails per minute', {
              required: true,
              inputMode: 'numeric',
              placeholder: LIMITS.perMinuteDefault,
            })}
          </div>
        </Section>

        <Section title="Access">
          <Checkbox
            label="Restricted to named users"
            checked={values.restricted}
            onChange={(event) => setField('restricted', event.target.checked)}
          />
          {values.restricted && (
            <Field label="Allowed users" error={usersError}>
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={values.allowedUserIds}
                  onChange={(next) => setField('allowedUserIds', next)}
                  options={userOptions}
                  placeholder="Nobody chosen"
                  searchPlaceholder="Search staff…"
                  loading={staff.loading}
                  disabled={staff.failed}
                  summaryNoun={{ singular: 'user', plural: 'users' }}
                  footnote={staff.hasMore ? 'Showing the first page of staff accounts.' : undefined}
                  invalid={Boolean(usersError)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          )}
        </Section>

        <Section title="Deliverability & status">
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('dkimSelector', 'DKIM selector', {
              maxLength: MAX_LENGTHS.dkimSelector,
            })}
            <div className="flex min-w-0 items-end pb-2">
              <Checkbox
                label="Active"
                checked={values.isActive}
                onChange={(event) => setField('isActive', event.target.checked)}
              />
            </div>
          </div>
        </Section>
      </form>
    </Modal>
  );
}
